import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserPlus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const ROLES = ["owner", "admin", "member", "viewer"];

export default function Members() {
  const { t } = useTranslation();
  const { currentOrg } = useOrg();
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [invite, setInvite] = useState({ email: "", role: "member" });

  const load = () => api.get("/members").then((r) => setMembers(r.data)).catch(() => {});
  useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

  const doInvite = async () => {
    try {
      await api.post("/members", invite);
      toast.success("Invited");
      setInvite({ email: "", role: "member" });
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const changeRole = async (uid, role) => {
    try { await api.patch(`/members/${uid}`, { role }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (uid) => {
    if (!window.confirm(t("members.confirmRemove"))) return;
    try { await api.delete(`/members/${uid}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title={t("members.title")}
        subtitle={t("members.subtitle")}
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="invite-member-button">
                <UserPlus size={16} className="mr-2" /> {t("members.invite")}
              </Button>
            </DialogTrigger>
            <DialogContent className="backdrop-blur-xl bg-background/80">
              <DialogHeader><DialogTitle className="font-heading">{t("members.inviteTitle")}</DialogTitle><DialogDescription className="sr-only">Invite a member by email</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t("common.email")}</Label>
                  <Input type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className="rounded-sm" data-testid="invite-email-input" />
                </div>
                <div className="space-y-2">
                  <Label>{t("common.role")}</Label>
                  <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.filter((r) => r !== "owner").map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={doInvite} data-testid="invite-submit-button">{t("members.invite")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("common.name")}</TableHead>
              <TableHead>{t("common.email")}</TableHead>
              <TableHead>{t("common.role")}</TableHead>
              <TableHead>{t("common.status")}</TableHead>
              <TableHead className="text-right">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.map((m) => (
              <TableRow key={m.user_id} data-testid={`member-row-${m.email}`}>
                <TableCell className="font-medium">{m.name}</TableCell>
                <TableCell className="font-mono text-xs">{m.email}</TableCell>
                <TableCell>
                  <Select value={m.role} onValueChange={(v) => changeRole(m.user_id, v)}>
                    <SelectTrigger className="rounded-sm w-32 h-8" data-testid={`role-select-${m.email}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Badge variant={m.status === "active" ? "default" : "secondary"} className="rounded-sm text-[10px] uppercase">{m.status}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => remove(m.user_id)} data-testid={`remove-member-${m.email}`}>
                    <Trash size={16} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
